import React, { useContext, useMemo, useState } from 'react';
import { TranslationKey } from './../../../../../../../../types/translation/TranslationKey';
import { ProductTransaction, ProductTransactionType } from '../../../../../../../../types/productTransaction';
import { Grid } from '@mui/material';
import { ProductMasterData } from '../../../../../../../../types/product_master_data/productMasterData';
import { OrderParentType } from '../../../../../../../../types/order';
import { t, translate } from '../../../../../../../../types/translation/Translator';
import { BinContext } from '../../../../../../../../context/BinContext';
import { OrderContext } from '../../../../../../../../context/OrderContext';
import { ProductContext } from '../../../../../../../../context/ProductContext';
import { BinStatusContext } from '../../../../../../../../context/BinStatusContext';
import { Container } from '../../../../../../../../types/container';
import { isValidDestinationContainer } from '../../../../../../../../util/containers.util';
import { ContainerContext } from '../../../../../../../../context/ContainerContext';
import { CompanyContext } from '../../../../../../../../context/CompanyContext';
import { ProductTransactionContext } from '../../../../../../../../context/ProductTransactionContext';
import CreateOrderCreateContainerModal from '../../Modals/CreateOrderCreateContainerModal';
import { SystemBin } from '../../../../../../../../types/bin';
import { DropdownSearchSelect } from '../../../../../../../components/Dropdown';
import { IconButton } from '../../../../../../../icons';
import { IconType } from '../../../../../../../icons/Icon';
import { UnitOfMeasure } from '../../../../../../../../types/uom';
import { ProcessTransactionProps } from '../ProcessOrderTransactionDefaultBody';
import { System } from '../../../../../../../../types/system';

interface ProductTransactionDestinationContainerProps {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;

  productMasterData?: ProductMasterData;
  container?: Container;

  disabled?: boolean;

  props: ProcessTransactionProps;
}

export default function ProductTransactionDestinationContainer({
  transaction,
  setTransaction,
  productMasterData,
  container,
  disabled = false,
  props,
}: ProductTransactionDestinationContainerProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { bins } = useContext(BinContext);
  const { orders } = useContext(OrderContext);
  const { containers } = useContext(ContainerContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { products } = useContext(ProductContext);
  const { productTransactions } = useContext(ProductTransactionContext);

  const [openCreateContainerModal, setOpenCreateContainerModal] = useState(false);

  const showContainer = useMemo(() => currentCompany.settings.featureToggles.containers.containers, [currentCompany]);

  const containersForProduct = useMemo(() => {
    const map = new Map<string, number>();
    [...(products.get(productMasterData?.id || '')?.values() || [])].forEach(p => {
      map.set(p.containerId || p.binId, parseFloat(p.unitQuantity || '0') || 0);
    });
    return map;
  }, [products, productMasterData]);

  if (transaction.type === ProductTransactionType.outbound || !showContainer) return null;
  if (
    transaction.type === ProductTransactionType.move &&
    transaction.statusId === System.ProductTransactionStatus.createdId
  ) {
    return null;
  }

  const items = [...containers.values()]
    .filter(c =>
      isValidDestinationContainer({
        container: c,
        stockLocationId: transaction.product.toStockLocationId || '',
        binStatuses,
        bins,
        binId: transaction.product.toBinId,
        containers,
        company: currentCompany,
      }),
    )
    .sort((a, b) => (containersForProduct.get(b.id) || 0) - (containersForProduct.get(a.id) || 0));

  const isDisabled =
    transaction.statusId !== System.ProductTransactionStatus.createdId ||
    disabled ||
    (orders.get(transaction.parentId || '')?.parentType === OrderParentType.task &&
      transaction.type === ProductTransactionType.inbound) ||
    !!transaction.containerTransactionId ||
    !!props.toContainerId;

  const handleSelect = (container: Container | null | undefined) => {
    if (
      container &&
      container.binId !== SystemBin.id &&
      !currentCompany.settings.featureToggles.containers.allowBinMismatch
    ) {
      transaction.product.toBinId = container.binId;
    }

    setTransaction(transaction => transaction.withToContainerId(container?.id));
  };

  const value = containers.get(transaction.product.toContainerId || '') || transaction.metadata?.toContainer;

  return (
    <>
      {openCreateContainerModal && (
        <CreateOrderCreateContainerModal
          open={openCreateContainerModal}
          setOpen={setOpenCreateContainerModal}
          onCreated={c => setTransaction(transaction => transaction.withToContainerId(c?.id))}
        />
      )}
      <Grid item xs={6}>
        <div className='flex h-full items-center gap-1'>
          <div className='flex-grow'>
            <DropdownSearchSelect
              disabled={isDisabled}
              placeholder={t(TranslationKey.destinationContainer).singular.label}
              label={t(TranslationKey.destinationContainer).singular.label}
              items={items}
              value={
                containers.get(transaction.product.toContainerId || '') ||
                (transaction.statusId !== System.ProductTransactionStatus.createdId
                  ? transaction.metadata?.toContainer
                  : null)
              }
              onSelect={handleSelect}
              textBuilder={item => `${item?.identifier}`}
              itemBuilder={
                productMasterData
                  ? item => (
                      <div className='flex justify-between'>
                        <div>
                          <p>{item?.identifier}</p>
                        </div>
                        <div>
                          <p className='text-sm text-gray-400'>{`${UnitOfMeasure.toQuantityWithSuffix(
                            productMasterData.unitOfMeasure,
                            (containersForProduct.get(item?.id || '') || 0).toString(),
                          )}`}</p>
                        </div>
                      </div>
                    )
                  : undefined
              }
            />
          </div>
          {!transaction.product.toContainerId && !disabled ? (
            <div className='flex h-full items-center pt-6'>
              <IconButton
                className='border border-gray-default'
                type={IconType.plus}
                onClick={() => setOpenCreateContainerModal(true)}
                text={translate({ key: TranslationKey.createContainer })}
              />
            </div>
          ) : null}
        </div>
      </Grid>
    </>
  );
}
